import React, { useEffect, useState } from 'react';
import { 
  Row, 
  Col, 
  Card, 
  Statistic, 
  Tabs, 
  Table, 
  Tag,
  Progress,
  Collapse,
  Spin,
  Alert,
  Select,
  Button,
  Typography,
  Space
} from 'antd';
import {
  DollarOutlined,
  ShoppingCartOutlined,
  UserOutlined,
  DownloadOutlined,
  RiseOutlined,
  FallOutlined
} from '@ant-design/icons';
import { Line, Bar, Pie } from 'react-chartjs-2';
import axios from 'axios';
import html2pdf from 'html2pdf.js';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const { Title: AntTitle, Text } = Typography;
const { TabPane } = Tabs;
const { Panel } = Collapse;
const { Option } = Select;

const API_BASE_URL = process.env.REACT_APP_API_URL || "https://agpipefitting.org.et/api";

const DashboardPage = () => {
  const [stats, setStats] = useState({ 
    totalSales: 0, 
    soldProducts: 0,
    activeCustomers: 0,
    avgOrderValue: 0
  });
  const [salesBreakdown, setSalesBreakdown] = useState({ 
    monthly: [], 
    quarterly: [], 
    yearly: [],
    categorical: {},
    geographic: {}
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeFrame, setTimeFrame] = useState('monthly');

  // Data processing functions
  const calculateBreakdowns = (salesData, products) => {
    const monthlyData = {};
    const quarterlyData = {};
    const yearlyData = {};
    const categoricalData = {};
    const geographicData = {};

    salesData.forEach(sale => {
      const date = new Date(sale.sale_date);
      const month = date.getMonth();
      const year = date.getFullYear();
      const quarter = Math.floor(month / 3) + 1;
      
      const monthYear = `${date.toLocaleString('default', { month: 'long' })} ${year}`;
      const quarterYear = `Q${quarter} ${year}`;
      const yearStr = `${year}`;

      updateTemporalData(monthlyData, monthYear, sale);
      updateTemporalData(quarterlyData, quarterYear, sale);
      updateTemporalData(yearlyData, yearStr, sale);

      const product = products.find(p => p.id === sale.product_id);
      if (product) {
        const category = product.category || 'Uncategorized';
        categoricalData[category] = (categoricalData[category] || 0) + (parseFloat(sale.total_price) || 0);
      }

      if (sale.shipping_state) {
        const state = sale.shipping_state.trim();
        geographicData[state] = (geographicData[state] || 0) + (parseFloat(sale.total_price) || 0);
      }
    });

    return {
      monthly: sortTemporalData(monthlyData, 'month'),
      quarterly: sortTemporalData(quarterlyData, 'quarter'),
      yearly: sortTemporalData(yearlyData, 'year'),
      categorical: sortByValue(categoricalData),
      geographic: sortByValue(geographicData)
    };
  };

  const updateTemporalData = (dataset, key, sale) => {
    if (!dataset[key]) {
      dataset[key] = { totalSales: 0, totalProducts: 0, transactions: 0 };
    }
    dataset[key].totalSales += parseFloat(sale.total_price) || 0;
    dataset[key].totalProducts += parseInt(sale.quantity) || 0;
    dataset[key].transactions++;
  };

  const sortTemporalData = (data, type) => {
    return Object.entries(data).sort(([aKey], [bKey]) => {
      if (type === 'month') return new Date(aKey) - new Date(bKey);
      if (type === 'quarter') {
        const [qA, yA] = aKey.split(' ');
        const [qB, yB] = bKey.split(' ');
        return yA - yB || parseInt(qA.slice(1)) - parseInt(qB.slice(1));
      }
      return parseInt(aKey) - parseInt(bKey);
    });
  };

  const sortByValue = (obj) => {
    return Object.entries(obj)
      .sort(([,a], [,b]) => b - a)
      .reduce((acc, [k, v]) => ({ ...acc, [k]: v }), {});
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [salesRes, productsRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/sales`),
          axios.get(`${API_BASE_URL}/products`)
        ]);

        const salesData = salesRes.data;
        const products = productsRes.data;

        const totalSales = salesData.reduce((sum, sale) => sum + (parseFloat(sale.total_price) || 0), 0);
        const soldProducts = salesData.reduce((sum, sale) => sum + (parseInt(sale.quantity) || 0), 0);
        const activeCustomers = new Set(salesData.map(sale => sale.customer_id)).size;
        const avgOrderValue = salesData.length > 0 ? totalSales / salesData.length : 0;

        const breakdowns = calculateBreakdowns(salesData, products);
        
        setStats({ totalSales, soldProducts, activeCustomers, avgOrderValue });
        setSalesBreakdown(breakdowns);

      } catch (err) {
        console.error("API Error:", err);
        setError('Failed to load dashboard data. Please check your network connection and API availability.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleDownloadPDF = () => {
    const element = document.getElementById("dashboard-report");
    html2pdf().from(element).set({
      margin: [15, 15],
      filename: `sales-dashboard-${new Date().toISOString().split('T')[0]}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'mm', format: 'a3', orientation: 'landscape' }
    }).save();
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' },
      tooltip: {
        backgroundColor: '#1A237E',
        titleColor: '#FFFFFF',
        bodyColor: '#FFFFFF',
        borderColor: 'rgba(255,255,255,0.1)',
        borderWidth: 1,
        padding: 12,
        callbacks: {
          label: (context) => ` ETB ${context.raw.toLocaleString()}`
        }
      }
    },
    scales: {
      x: { 
        grid: { display: false },
        ticks: { color: '#6B7280' }
      },
      y: {
        beginAtZero: true,
        ticks: { 
          color: '#6B7280',
          callback: value => `ETB ${value.toLocaleString()}` 
        }
      }
    }
  };

  return (
    <div className="dashboard-page" id="dashboard-report">
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <AntTitle level={3} style={{ margin: 0 }}>AG Pipe Fittings Sales Dashboard</AntTitle>
          <Text type="secondary">Comprehensive sales analytics and business intelligence</Text>
        </Col>
        <Col>
          <Space>
            <Select
              defaultValue="monthly"
              onChange={setTimeFrame}
              style={{ width: 120 }}
              size="large"
            >
              <Option value="monthly">Monthly</Option>
              <Option value="quarterly">Quarterly</Option>
              <Option value="yearly">Yearly</Option>
            </Select>
            <Button 
              type="primary" 
              icon={<DownloadOutlined />}
              onClick={handleDownloadPDF}
              size="large"
            >
              Export
            </Button>
          </Space>
        </Col>
      </Row>

      {error && (
        <Alert 
          message="Data Loading Error"
          description={error}
          type="error" 
          showIcon
          closable
          style={{ marginBottom: 24 }}
        />
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" tip="Loading Dashboard Data..." />
        </div>
      ) : (
        <>
          <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
            {[
              {
                title: 'Total Sales',
                value: stats.totalSales,
                prefix: <DollarOutlined />,
                suffix: 'ETB',
                trend: 12.5,
                color: '#1890ff'
              },
              {
                title: 'Units Sold',
                value: stats.soldProducts,
                prefix: <ShoppingCartOutlined />,
                trend: 8.3,
                color: '#52c41a'
              },
              {
                title: 'Active Clients',
                value: stats.activeCustomers,
                prefix: <UserOutlined />,
                trend: 5,
                color: '#faad14'
              },
              {
                title: 'Avg. Order Value',
                value: stats.avgOrderValue,
                prefix: <DollarOutlined />,
                suffix: 'ETB',
                trend: -2.1,
                color: '#13c2c2'
              }
            ].map((stat, index) => (
              <Col key={index} xs={24} sm={12} lg={6}>
                <Card 
                  bordered={false}
                  style={{ borderRadius: 12, boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                >
                  <Statistic
                    title={stat.title}
                    value={stat.value}
                    prefix={stat.prefix}
                    suffix={stat.suffix}
                    valueStyle={{ color: stat.color, fontSize: 24 }}
                  />
                  <div style={{ marginTop: 12 }}>
                    <Tag 
                      icon={stat.trend > 0 ? <RiseOutlined /> : <FallOutlined />} 
                      color={stat.trend > 0 ? 'success' : 'error'}
                    >
                      {stat.trend > 0 ? '+' : ''}{stat.trend}%
                    </Tag>
                    <Text type="secondary" style={{ marginLeft: 8 }}>
                      {stat.title.includes('Client') ? 'new clients' : 
                      stat.trend > 0 ? 'increase' : 'decrease'}
                    </Text>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>

          <Card
            bordered={false}
            style={{ marginBottom: 24, borderRadius: 12 }}
            title={
              <AntTitle level={5} style={{ margin: 0 }}>
                Sales Performance Trend
              </AntTitle>
            }
          >
            <div style={{ height: 400 }}>
              <Line 
                data={{
                  labels: salesBreakdown[timeFrame].map(([period]) => period),
                  datasets: [{
                    label: `Total Sales (ETB)`,
                    data: salesBreakdown[timeFrame].map(([, stats]) => stats.totalSales),
                    borderColor: '#1890ff',
                    backgroundColor: 'rgba(24, 144, 255, 0.1)',
                    tension: 0.3,
                    borderWidth: 2
                  }]
                }} 
                options={chartOptions}
              />
            </div>
          </Card>

          <Tabs defaultActiveKey="1" size="large">
            <TabPane tab="Temporal Analysis" key="1">
              <Collapse accordion ghost>
                {['monthly', 'quarterly', 'yearly'].map(period => (
                  <Panel 
                    header={`${period.charAt(0).toUpperCase() + period.slice(1)} Breakdown`} 
                    key={period}
                  >
                    <Table 
                      columns={[
                        { title: 'Period', dataIndex: 'period' },
                        { 
                          title: 'Total Sales', 
                          dataIndex: 'totalSales',
                          render: v => `ETB ${Number(v).toLocaleString()}`,
                          align: 'right'
                        },
                        { title: 'Units Sold', dataIndex: 'totalProducts', align: 'right' },
                        { title: 'Transactions', dataIndex: 'transactions', align: 'right' },
                        { 
                          title: 'Avg. Order', 
                          render: (_, r) => `ETB ${(r.totalSales / r.transactions).toFixed(2)}`,
                          align: 'right'
                        }
                      ]}
                      dataSource={salesBreakdown[period].map(([p, s]) => ({
                        key: p,
                        period: p,
                        ...s
                      }))}
                      pagination={false}
                      bordered
                    />
                  </Panel>
                ))}
              </Collapse>
            </TabPane>

            <TabPane tab="Product Analysis" key="2">
              <Row gutter={[24, 24]}>
                <Col xs={24} md={12}>
                  <Card title="Category Distribution">
                    <div style={{ height: 300 }}>
                      <Pie
                        data={{
                          labels: Object.keys(salesBreakdown.categorical),
                          datasets: [{
                            data: Object.values(salesBreakdown.categorical),
                            backgroundColor: [
                              '#1890ff',
                              '#52c41a',
                              '#faad14',
                              '#f5222d',
                              '#722ed1'
                            ]
                          }]
                        }}
                        options={chartOptions}
                      />
                    </div>
                  </Card>
                </Col>
                <Col xs={24} md={12}>
                  <Card title="Category Performance">
                    <Table
                      columns={[
                        { title: 'Category', dataIndex: 'category' },
                        { 
                          title: 'Sales Value', 
                          render: (_, r) => `ETB ${r.value.toLocaleString()}`,
                          align: 'right'
                        },
                        { 
                          title: 'Market Share', 
                          render: (_, r) => `${((r.value / stats.totalSales) * 100).toFixed(1)}%`,
                          align: 'right'
                        }
                      ]}
                      dataSource={Object.entries(salesBreakdown.categorical).map(([c, v]) => ({
                        key: c,
                        category: c,
                        value: v
                      }))}
                      pagination={false}
                    />
                  </Card>
                </Col>
              </Row>
            </TabPane>

            <TabPane tab="Regional Analysis" key="3">
              <Row gutter={[24, 24]}>
                <Col xs={24} md={16}>
                  <Card title="Geographic Sales Distribution">
                    <div style={{ height: 400 }}>
                      <Bar
                        data={{
                          labels: Object.keys(salesBreakdown.geographic),
                          datasets: [{
                            label: 'Sales (ETB)',
                            data: Object.values(salesBreakdown.geographic),
                            backgroundColor: '#1890ff'
                          }]
                        }}
                        options={chartOptions}
                      />
                    </div>
                  </Card>
                </Col>
                <Col xs={24} md={8}>
                  <Card title="Top Performing Regions">
                    {Object.entries(salesBreakdown.geographic)
                      .slice(0, 5)
                      .map(([region, value]) => (
                        <div key={region} style={{ marginBottom: 16 }}>
                          <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between',
                            marginBottom: 4
                          }}>
                            <Text strong>{region}</Text>
                            <Text>ETB {value.toLocaleString()}</Text>
                          </div>
                          <Progress
                            percent={(value / stats.totalSales) * 100}
                            showInfo={false}
                            strokeColor="#1890ff"
                          />
                        </div>
                      ))}
                  </Card>
                </Col>
              </Row>
            </TabPane>
          </Tabs>
        </>
      )}
    </div>
  );
};

export default DashboardPage;